import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion as Motion, AnimatePresence } from "motion/react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { configSecondaryInfo } from "@/features/evento/eventoThunks";
import CollapsibleDescription from "@/components/CrearDetailEvento/CardAmbassadors/CardEmbajadores/CollapsibleDescription";

export default function EventDescription({
  initialDescription = "",
  onSave,
  eventIsPublished,
}) {
  const dispatch = useDispatch();
  const { idEvento } = useParams();
  const { isLoading: eventoLoading } = useSelector((state) => state.evento);

  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [description, setDescription] = useState(initialDescription);
  const [previewDescription, setPreviewDescription] =
    useState(initialDescription);

  useEffect(() => {
    if (initialDescription) {
      setDescription(initialDescription);
      setPreviewDescription(initialDescription);
    }
  }, [initialDescription]);

  const handleSave = async () => {
    if (!idEvento) {
      console.error("No se encontró el ID del evento");
      return;
    }

    setIsSaving(true);
    setSaveSuccess(false);

    try {
      const configData = {
        descripcion: description,
      };

      console.log("Guardando descripción del evento:", configData);

      await dispatch(
        configSecondaryInfo({
          uidEvento: idEvento,
          configData,
        })
      ).unwrap();

      setPreviewDescription(description);
      setIsEditMode(false);
      setSaveSuccess(true);

      if (onSave) {
        onSave(description);
      }

      // Mostrar mensaje de éxito por unos segundos
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Error al guardar descripción:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setDescription(previewDescription);
    setIsEditMode(false);
  };

  const characterCount = description.length;
  const maxCharacters = 10000;

  return (
    <div className="relative bg-aw-bluedark-95 rounded-2xl p-4 sm:p-6 flex flex-col items-center w-full min-h-[20dvh]">
      {/* Edit/Preview Toggle Button */}
      {isEditMode ? (
        <></>
      ) : !eventIsPublished ? (
        <Motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsEditMode(!isEditMode)}
          className="absolute top-4 right-4 z-50 px-4 py-2 rounded-full bg-aw-green/85 text-aw-bluedark font-medium hover:bg-aw-green transition duration-300 flex items-center gap-2"
        >
          <FontAwesomeIcon
            icon={isEditMode ? "fa-solid fa-eye" : "fa-solid fa-edit"}
          />
          {isEditMode ? "Vista Previa" : "Editar"}
        </Motion.button>
      ) : (
        <></>
      )}

      <AnimatePresence mode="wait">
        {isEditMode ? (
          <Motion.div
            key="edit-mode"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="w-full space-y-4"
          >
            <div className="text-white font-bold text-xl text-center mb-4">
              Descripción del Evento
            </div>

            {/* Description Textarea */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <FontAwesomeIcon
                  icon="fa-solid fa-align-left"
                  className="mr-2"
                />
                Descripción
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Escribe una descripción detallada del evento..."
                maxLength={maxCharacters}
                rows={10}
                className="w-full px-4 py-3 bg-aw-bluedark border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-aw-green transition duration-300 resize-none"
              />
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-gray-400">
                  Usa saltos de línea para dar formato a tu texto
                </span>
                <span
                  className={`text-sm ${
                    characterCount > maxCharacters * 0.9
                      ? "text-orange-400"
                      : "text-gray-400"
                  }`}
                >
                  {characterCount} / {maxCharacters}
                </span>
              </div>
            </div>

            {/* Save and Cancel Buttons */}
            <div className="flex gap-3 pt-4">
              <Motion.button
                whileHover={{ scale: isSaving ? 1 : 1.02 }}
                whileTap={{ scale: isSaving ? 1 : 0.98 }}
                onClick={handleSave}
                disabled={isSaving || eventoLoading}
                className={`flex-1 py-3 font-bold rounded-lg transition duration-300 flex items-center justify-center gap-2 ${
                  saveSuccess
                    ? "bg-green-600 text-white"
                    : isSaving || eventoLoading
                    ? "bg-gray-500 text-gray-300 cursor-not-allowed"
                    : "bg-aw-green/85 text-aw-bluedark hover:bg-aw-green"
                }`}
              >
                {isSaving || eventoLoading ? (
                  <>
                    <FontAwesomeIcon
                      icon="fa-solid fa-spinner"
                      className="animate-spin"
                    />
                    Guardando...
                  </>
                ) : saveSuccess ? (
                  <>
                    <FontAwesomeIcon icon="fa-solid fa-check" />
                    ¡Guardado!
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon="fa-solid fa-save" />
                    Guardar Descripción
                  </>
                )}
              </Motion.button>
              <Motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleCancel}
                disabled={isSaving || eventoLoading}
                className={`flex-1 py-3 font-bold rounded-lg transition duration-300 flex items-center justify-center gap-2 ${
                  isSaving || eventoLoading
                    ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                    : "bg-gray-600 text-white hover:bg-gray-500"
                }`}
              >
                <FontAwesomeIcon icon="fa-solid fa-times" />
                Cancelar
              </Motion.button>
            </div>
          </Motion.div>
        ) : (
          <Motion.div
            key="preview-mode"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="w-full flex flex-col items-center"
          >
            <div className="text-center font-semibold text-white text-lg mb-4">
              Descripción
            </div>

            {previewDescription ? (
              <CollapsibleDescription text={previewDescription} />
            ) : (
              <div className="text-center py-8 text-gray-400">
                <FontAwesomeIcon
                  icon="fa-solid fa-file-alt"
                  className="text-4xl mb-2"
                />
                <p>No hay descripción del evento</p>
                <p className="text-sm mt-1">
                  Haz clic en "Editar" para agregar
                </p>
              </div>
            )}
          </Motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
